import { sendAnalyticsEvent } from '~/lib/analytics.client';
import type { AnalyzerWorker } from '~/lib/static-analysis/analyzer';
import type { ModuleDeclaration, ImportDeclaration } from '~/lib/static-analysis/esm';
import type MagicString from '~/lib/static-analysis/magic';
import { ModuleGraph } from '~/lib/static-analysis/module-graph';
import { renderESM } from '~/lib/static-analysis/render-esm';
import type { TopLevelIdent } from '~/lib/static-analysis/top-level';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import { addWorkDir } from '~/utils/workDir';

export async function transformExports(code: MagicString, path: string, graph: ModuleGraph, analyzer: AnalyzerWorker) {
  const fullPath = addWorkDir(path);

  for (const source of graph.getDependents(path).values()) {
    const depModules = graph.getModuleDeclarations(source);

    for (const decl of depModules) {
      if (decl.type === 'import') {
        const target = graph.resolver.resolve(source, decl.module.path);

        if (target && target === fullPath) {
          const modules = graph.getModuleDeclarations(fullPath);

          await transformDefault(code, fullPath, modules, analyzer, decl);
          await transformNamed(code, fullPath, modules, analyzer, decl);
        }
      }
    }
  }
}

async function transformDefault(
  code: MagicString,
  fullPath: string,
  modules: ModuleDeclaration[],
  analyzer: AnalyzerWorker,
  decl: ImportDeclaration,
) {
  if (!decl.defaultIdent) {
    return;
  }

  // someone depends on a default export in this file

  if (hasDefault(modules)) {
    // already has a default export
    return;
  }

  if (fullPath.endsWith('.svelte') || fullPath.endsWith('.vue')) {
    // TODO: find out how to add a default export to svelte/vue
    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.MissingExportUnsupportedFormat,
        properties: {
          extension: fullPath.slice(fullPath.lastIndexOf('.')),
          type: 'default',
        },
      },
    });

    return;
  }

  const { idents, scopeLevel } = await analyzer.extractTopLevelVars(code.str, fullPath);

  if (scopeLevel !== 0) {
    return;
  }

  const ident = findIdent(decl.defaultIdent, idents);

  // a default may have been added since we last checked, so we need to check again after async actions have completed
  if (ident && !hasDefault(modules)) {
    const exportStr = `\n\nexport default ${ident}`;

    code.append(exportStr);
    modules.push({
      type: 'export',
      default: true,
      startIndex: code.str.length,
      endIndex: code.str.length + exportStr.length,
    });
  } else if (!ident) {
    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.NothingToDefaultExport,
        properties: {
          content: code.str,
        },
      },
    });
  }
}

function hasDefault(modules: ModuleDeclaration[]) {
  return modules.some((mod) => mod.type === 'export' && mod.default);
}

async function transformNamed(
  code: MagicString,
  fullPath: string,
  modules: ModuleDeclaration[],
  analyzer: AnalyzerWorker,
  decl: ImportDeclaration,
) {
  if (!decl.namedImports) {
    return;
  }

  const importIdents = new Set(decl.namedImports.map((bind) => bind.specifier));

  for (const decl of modules) {
    if (decl.type === 'export') {
      if (decl.constIdent) {
        importIdents.delete(decl.constIdent);
      }

      decl.namedExports?.forEach((bind) => {
        importIdents.delete(bind.specifier);
      });
    } else if (decl.type === 'classExport' || decl.type === 'functionExport') {
      importIdents.delete(decl.ident);
    }
  }

  if (importIdents.size === 0) {
    return;
  }

  const { idents, scopeLevel } = await analyzer.extractTopLevelVars(code.str, fullPath);

  if (scopeLevel !== 0) {
    return;
  }

  const toAdd = [];

  // for each missing named export identifier
  for (const missing of importIdents.values()) {
    if (idents.some(([, ident]) => ident === missing)) {
      toAdd.push(missing);
    }
  }

  if (toAdd.length > 0) {
    if (fullPath.endsWith('.svelte') || fullPath.endsWith('.vue')) {
      // TODO: find out how to add an export to svelte/vue
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.MissingExportUnsupportedFormat,
          properties: {
            extension: fullPath.slice(fullPath.lastIndexOf('.')),
            type: 'named',
          },
        },
      });

      return;
    }

    const missingDecl = {
      type: 'export',
      namedExports: toAdd.map((specifier) => ({ specifier })),
      startIndex: code.str.length,
      endIndex: code.str.length,
    } satisfies ModuleDeclaration;

    modules.push(missingDecl);
    code.append('\n\n' + renderESM(missingDecl));
  }
}

function findIdent(importName: string, idents: TopLevelIdent[]) {
  // use if the same name exists as in the dependent
  if (idents.some(([, ident]) => ident === importName)) {
    return importName;
  }

  // start looking from the bottom
  idents.reverse();

  // use the lowest function
  const func = idents.find(([type]) => type === 'function');

  if (func) {
    return func[1];
  }

  // use the lowest anything
  return idents[0]?.[1];
}
